// React hook that mirrors a boat's setup session into the cloud reveal
// (cloud-ftue setup-relay; reveal task 6.2 / 7.x).
//
// Two lanes, exactly the design's snapshot-authoritative model (D5):
//
//   1. BOOTSTRAP — GET /v1/boats/:boatId/setup returns the latest stored
//      `StoredSetupSession`. 404 `no_setup` is NOT an error: it is the
//      "waiting for your boat" state the reveal renders before the boat has
//      mirrored anything (connecting grace, task 7.4).
//   2. LIVE — subscribe `boat.<id>.setup` on the user-WS. Frames arrive as
//      { type:'setup', payload } where payload is one of:
//        { kind:'snapshot', session, emittedAt }              ← authoritative
//        { kind:'command_status', commandKey, command, phase, … } ← liveness
//      The snapshot is authoritative; we keep the newest by `emittedAt` so an
//      out-of-order frame never regresses the view. command_status beats are a
//      garnish used only to correlate a down-command's accepted→complete/
//      rejected lifecycle (task 4.5) — the real outcome rides the next snapshot.
//
// Auth is the cookie on the WS upgrade (same-origin), exactly like
// useBoatLiveData / useSourceAnalysis. Reconnect uses the same bounded backoff.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import type { AlarmDecision, RevealAlarm } from './revealStaging';
import { beforeWsReconnect } from './wsReconnect';

/**
 * The slice of the boat-owned `SetupSession` the reveal reads. The wire payload
 * is a passthrough (`session: unknown` server-side), so this is intentionally
 * loose — unknown future fields are tolerated.
 */
export interface RevealSetupSession {
  id: string;
  status: 'active' | 'complete' | 'error';
  phase: string;
  candidates?: unknown[];
  recommendations?: RevealAlarm[];
  healthIssues?: unknown[];
  /**
   * Gateways the boat's inline config-harvest (B3) couldn't read without a
   * device login — mirrors core's `ConfigHarvestNeedsCredentials[]`
   * VERBATIM (contract authority: core/src/api/setup/config-harvest.ts's
   * route summary). Unknown-shape at the wire like the other snapshot
   * fields above; parsed defensively downstream (`lib/revealStaging`
   * `credentialsNeeded`). Carries NO secret material — target ids / vendor
   * / a best-effort host only.
   */
  needsCredentials?: unknown[];
  snapshots?: unknown[];
  answers?: unknown[];
  lastError?: string | null;
  [key: string]: unknown;
}

/** A down-command liveness beat (task 4.5). */
export interface SetupCommandStatus {
  commandKey: string;
  command:
    | 'setup_reanalyze'
    | 'setup_apply'
    | 'setup_harvest_credentials'
    | 'setup_record_decisions'
    | string;
  phase: 'accepted' | 'complete' | 'rejected';
  sessionId: string;
  at?: string;
  detail?: string;
}

export type SetupConnection = 'connecting' | 'open' | 'closed';

export type BoatSetupState =
  | { kind: 'loading' }
  | { kind: 'waiting' } // 404 no_setup — boat hasn't mirrored a session yet
  | { kind: 'ready'; session: RevealSetupSession; emittedAt: string }
  | { kind: 'error'; message: string };

export interface UseBoatSetup {
  state: BoatSetupState;
  /** WS connection state — drives the reveal's "reconnecting" degrade (7.4). */
  connection: SetupConnection;
  /** The most recent command_status beat across all commands. */
  lastCommand: SetupCommandStatus | null;
  /** Latest beat per commandKey, for correlating a specific down-command. */
  commandByKey: ReadonlyMap<string, SetupCommandStatus>;
  refresh: () => void;
  /**
   * Relay the `setup_rescan` verb — restart the boat's gateway scan (the
   * no-gateway hard stop's only action, reveal-gateways-onetab). Resolves on a
   * relayed (202) response; rejects (e.g. 409 `boat_offline`) so the caller can
   * surface the offline copy. The boat rolls back to `gateway_scan` and re-runs
   * discovery, mirroring the new scan's snapshots back up this lane.
   */
  rescan: (sessionId: string) => Promise<void>;
  /**
   * Relay the `setup_record_decisions` verb (reveal-alarm-review §5.2) — record
   * per-recommendation accept/reject decisions on the boat (writes
   * `recommendationChoice`, the same durable model desktop uses). Resolves
   * with the relay's `idempotencyKey` so the caller can correlate the
   * `command_status` beat via {@link commandByKey} and know when it is safe to
   * fire `apply`. Rejects on a failed relay (e.g. 409 `boat_offline`).
   */
  recordDecisions: (sessionId: string, decisions: AlarmDecision[]) => Promise<string>;
  /**
   * Relay the EXISTING `setup_apply` verb — unchanged payload (`{ sessionId }`,
   * task 1.3/3.4). Compiles whatever decisions are currently persisted
   * boat-side, so callers must await a `recordDecisions` completion first.
   * Resolves with the relay's `idempotencyKey`, mirroring {@link recordDecisions}.
   */
  apply: (sessionId: string) => Promise<string>;
  /**
   * Relay the EXISTING `setup_harvest_credentials` verb (B3 — the
   * config-harvest credentials round trip) — unchanged payload
   * (`{ sessionId, targetId, bundle }`). Resolves with the relay's
   * `idempotencyKey`, mirroring {@link recordDecisions} / {@link apply}, so
   * the caller can correlate the resulting `command_status` beat via
   * {@link commandByKey}. Callers MUST track that correlation in a state
   * distinct from the arming sequence's (record-decisions/apply) — a lodge
   * can be in flight alongside "Start watching", and reusing one pending-key
   * slot would collide the two independent commands.
   */
  harvestCredentials: (
    sessionId: string,
    targetId: string,
    bundle: Record<string, string>
  ) => Promise<string>;
}

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 16_000;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseSession(raw: unknown): RevealSetupSession | null {
  if (!isObject(raw)) return null;
  if (typeof raw.id !== 'string') return null;
  if (raw.status !== 'active' && raw.status !== 'complete' && raw.status !== 'error') return null;
  if (typeof raw.phase !== 'string') return null;
  return raw as RevealSetupSession;
}

function parseCommandStatus(raw: unknown): SetupCommandStatus | null {
  if (!isObject(raw)) return null;
  if (typeof raw.commandKey !== 'string' || typeof raw.command !== 'string') return null;
  if (raw.phase !== 'accepted' && raw.phase !== 'complete' && raw.phase !== 'rejected') return null;
  if (typeof raw.sessionId !== 'string') return null;
  const out: SetupCommandStatus = {
    commandKey: raw.commandKey,
    command: raw.command,
    phase: raw.phase,
    sessionId: raw.sessionId,
  };
  if (typeof raw.at === 'string') out.at = raw.at;
  if (typeof raw.detail === 'string') out.detail = raw.detail;
  return out;
}

export function useBoatSetup(boatId: string): UseBoatSetup {
  const [state, setState] = useState<BoatSetupState>({ kind: 'loading' });
  const [connection, setConnection] = useState<SetupConnection>('connecting');
  const [lastCommand, setLastCommand] = useState<SetupCommandStatus | null>(null);
  const [tick, setTick] = useState(0);

  // The newest emittedAt we've applied — guards against out-of-order frames
  // regressing the authoritative snapshot (design D5).
  const newestEmittedRef = useRef<string>('');
  const commandByKeyRef = useRef<Map<string, SetupCommandStatus>>(new Map());

  // Reset per-boat state + the monotonic/correlation refs when the boat changes
  // — the /reveal route component is reused across ?boatId=A → ?boatId=B, so
  // without this B would inherit A's newest-emittedAt (dropping B's first
  // snapshot) and A's command beats (reading B as already applied). Done during
  // render: the documented "adjust state when an input changes" pattern, so the
  // bootstrap/live effects re-run for B against clean refs.
  const prevBoatIdRef = useRef(boatId);
  if (prevBoatIdRef.current !== boatId) {
    prevBoatIdRef.current = boatId;
    newestEmittedRef.current = '';
    commandByKeyRef.current = new Map();
    setState({ kind: 'loading' });
    setLastCommand(null);
    setConnection('connecting');
  }

  // Stable identity so callers can use it as an effect dependency / interval
  // callback without re-subscribing every render (the reveal polls it while the
  // alarm build is in flight).
  const refresh = useCallback((): void => setTick((t) => t + 1), []);

  // Relay the scan-restart verb (reveal-gateways-onetab D4). Fire the POST; on
  // success nudge a re-read so the scanning beat picks up the rolled-back
  // (gateway_scan) snapshot promptly even if the WS push lags. Errors propagate.
  const rescan = useCallback(
    async (sessionId: string): Promise<void> => {
      await api(`/v1/boats/${boatId}/setup/rescan`, {
        method: 'POST',
        body: { sessionId },
      });
      setTick((t) => t + 1);
    },
    [boatId]
  );

  // Relay `setup_record_decisions` (reveal-alarm-review). Every setup verb
  // POST returns 202 `{ status:'relayed', delivered, idempotencyKey }`; the key
  // is what lets the caller correlate the command_status beat this same POST
  // eventually produces (task 4.5's commandByKey lane), so it's returned
  // rather than swallowed like rescan's void.
  const recordDecisions = useCallback(
    async (sessionId: string, decisions: AlarmDecision[]): Promise<string> => {
      const res = await api<{ idempotencyKey: string }>(`/v1/boats/${boatId}/setup/decisions`, {
        method: 'POST',
        body: { sessionId, decisions },
      });
      return res.idempotencyKey;
    },
    [boatId]
  );

  // Relay the EXISTING `setup_apply` verb — payload is unchanged (task 3.4).
  const apply = useCallback(
    async (sessionId: string): Promise<string> => {
      const res = await api<{ idempotencyKey: string }>(`/v1/boats/${boatId}/setup/apply`, {
        method: 'POST',
        body: { sessionId },
      });
      return res.idempotencyKey;
    },
    [boatId]
  );

  // Relay the EXISTING `setup_harvest_credentials` verb (B3) — unchanged
  // payload/route (`/setup/harvest-credentials`, cloud/src/api/setup/routes.ts).
  const harvestCredentials = useCallback(
    async (
      sessionId: string,
      targetId: string,
      bundle: Record<string, string>
    ): Promise<string> => {
      const res = await api<{ idempotencyKey: string }>(
        `/v1/boats/${boatId}/setup/harvest-credentials`,
        {
          method: 'POST',
          body: { sessionId, targetId, bundle },
        }
      );
      return res.idempotencyKey;
    },
    [boatId]
  );

  // Bootstrap from REST. Re-runs on boatId / manual refresh.
  useEffect(() => {
    if (boatId === '') return;
    let cancelled = false;
    void (async () => {
      try {
        const stored = await api<{ session?: unknown; emittedAt?: string }>(
          `/v1/boats/${boatId}/setup`
        );
        if (cancelled) return;
        const session = parseSession(stored.session);
        const emittedAt = typeof stored.emittedAt === 'string' ? stored.emittedAt : '';
        if (session === null) {
          setState({ kind: 'error', message: 'Received an unreadable setup snapshot.' });
          return;
        }
        // Only apply if it's at least as new as what live frames may have set.
        if (emittedAt >= newestEmittedRef.current) {
          newestEmittedRef.current = emittedAt;
          setState({ kind: 'ready', session, emittedAt });
        }
      } catch (err) {
        if (cancelled) return;
        const e = err as { status?: number; message?: string };
        if (e.status === 404) {
          // Don't clobber a snapshot a live frame already delivered.
          setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'waiting' }));
          return;
        }
        const message =
          e.status === 403
            ? 'You do not have access to this boat.'
            : e.status === 401
              ? 'Please sign in again.'
              : (e.message ?? 'Failed to load your boat’s setup.');
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId, tick]);

  // Live lane: subscribe boat.<id>.setup; apply snapshots monotonically and
  // record command_status beats.
  useEffect(() => {
    if (boatId === '' || typeof WebSocket === 'undefined') return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = BACKOFF_INITIAL_MS;
    let cancelled = false;
    // Track whether we've connected before, so a RECONNECT (not the first open)
    // re-bootstraps the authoritative store — a snapshot mirrored while we were
    // in the backoff window is live-only (no WS replay), so re-read GET to
    // self-heal the gap (design D5).
    let hasOpened = false;

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = backoffMs;
      backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
      setConnection('closed');
      reconnectTimer = setTimeout(connect, delay);
    }

    function handleSetupFrame(payload: unknown): void {
      if (!isObject(payload)) return;
      if (payload.kind === 'snapshot') {
        const session = parseSession(payload.session);
        const emittedAt = typeof payload.emittedAt === 'string' ? payload.emittedAt : '';
        if (session === null) return;
        // Monotonic on emittedAt — never regress below the newest applied.
        if (emittedAt < newestEmittedRef.current) return;
        newestEmittedRef.current = emittedAt;
        setState({ kind: 'ready', session, emittedAt });
      } else if (payload.kind === 'command_status') {
        const beat = parseCommandStatus(payload);
        if (beat === null) return;
        commandByKeyRef.current.set(beat.commandKey, beat);
        setLastCommand(beat);
      } else if (payload.kind === 'snapshot_changed') {
        // Cross-host doorbell (ADR 0133): the snapshot body was too large for
        // the NOTIFY bridge, so the frame says only "something changed" —
        // re-read the persisted store, the same self-heal the reconnect path
        // uses. Monotonicity is preserved by the bootstrap GET's own guard.
        setTick((t) => t + 1);
      }
    }

    function connect(): void {
      if (cancelled) return;
      setConnection('connecting');
      const url = new URL('/v1/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffMs = BACKOFF_INITIAL_MS;
        setConnection('open');
        if (hasOpened) {
          // Reconnected after a gap — re-read the persisted store so a snapshot
          // mirrored while we were offline isn't lost (the bus fan-out has no
          // replay). Bumping tick re-runs only the bootstrap GET effect.
          setTick((t) => t + 1);
        }
        hasOpened = true;
        try {
          ws.send(JSON.stringify({ type: 'subscribe', topic: `boat.${boatId}.setup` }));
        } catch {
          // socket gone; onclose will retry
        }
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return;
        try {
          const frame = JSON.parse(ev.data) as { type?: string; payload?: unknown };
          if (frame.type === 'setup') handleSetupFrame(frame.payload);
        } catch {
          /* ignore malformed frame */
        }
      };
      ws.onclose = (ev) => {
        socket = null;
        // Renew before retrying when the cloud closed us for an expired access
        // token (task ws_session_expiry): reconnecting with the cookie it just
        // refused only earns another refusal, and the screen would sit stale
        // through the backoff. Reconnects either way.
        void beforeWsReconnect(ev.code).then(scheduleReconnect, scheduleReconnect);
      };
      ws.onerror = () => {
        // onclose handles reconnect.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          /* noop */
        }
        socket = null;
      }
    };
  }, [boatId]);

  return {
    state,
    connection,
    lastCommand,
    commandByKey: commandByKeyRef.current,
    refresh,
    rescan,
    recordDecisions,
    apply,
    harvestCredentials,
  };
}
